import { lazy, Suspense } from "react";
import HeroSection from "./components/HeroSection";
import TennesseeCommunitiesWeServe from "./components/CommunitiesWeServe";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";
import CitySEOHead from "@/components/feature/CitySEOHead";

const CitiesDirectory = lazy(() => import("./components/CitiesDirectory"));
const StateResources = lazy(() => import("./components/StateResources"));
const FAQSection = lazy(() => import("./components/FAQSection"));
const FinalCTA = lazy(() => import("./components/FinalCTA"));

const servicesList = [
  { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
  { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
  { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
  { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
  { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
  { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
];

const tnFaqs = [
  {
    q: "Does Tennessee have a passport agency?",
    a: "No — Tennessee does not have a regional passport agency within state borders. The closest regional passport agency is the Atlanta Passport Agency (230 Peachtree Street NW, Suite 1000, Atlanta, GA 30303), serving all 95 Tennessee counties. Appointments are required. Fast Passport Center's courier service eliminates the need for any Tennessee residents to travel to Atlanta.",
  },
  {
    q: "How fast can I get a passport in Tennessee?",
    a: "With Fast Passport Center, Tennessee residents can get emergency service in as little as 24 hours or standard expedited processing in 3 to 9 business days. Routine service through the post office takes 6 to 13 weeks plus mailing time.",
  },
  {
    q: "Can Memphis and Knoxville residents use the Atlanta Passport Agency?",
    a: "Yes — the Atlanta Passport Agency serves all of Tennessee. Atlanta is approximately 380 miles from Memphis (6 hours), 200 miles from Knoxville (3 hours), and 120 miles from Chattanooga (2 hours). Fast Passport Center's courier service handles the hand-delivery for all Tennessee residents, eliminating the need for any long drives.",
  },
];

export default function TennesseePage() {
  return (
    <>
      <CitySEOHead
        city="Tennessee"
        state="Tennessee"
        stateAbbr="TN"
        route="/states/tennessee"
        title="Expedited & Emergency Passport Services Tennessee | Nashville TN Passport Agency | Fast Passport Center"
        description="Fast Passport Center provides emergency, expedited, and same-day passport services across all Tennessee cities. Serving Nashville, Memphis, Knoxville, and Chattanooga. Direct access to the Atlanta Passport Agency — the closest regional agency to Tennessee. Courier service covers all 95 Tennessee counties."
        geoPlacename="Tennessee"
        geoPosition="35.5175;-86.5804"
        faqs={tnFaqs}
        servicesSchema={servicesList}
        keywords="expedited passport Tennessee, emergency passport Tennessee, same day passport TN, fast passport Nashville, passport renewal Memphis, passport service Knoxville, Atlanta Passport Agency"
      />
      <div className="min-h-screen bg-white">
        <HeroSection />
        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-gray-50">
              <div className="animate-pulse text-gray-400 text-sm">Loading cities...</div>
            </div>
          }
        >
          <CitiesDirectory />
        </Suspense>
        <TennesseeCommunitiesWeServe />
        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-white">
              <div className="animate-pulse text-gray-400 text-sm">Loading resources...</div>
            </div>
          }
        >
          <StateResources />
        </Suspense>

        {/* How We Serve Tennessee */}
        <section className="bg-[#F7F8FA] pt-20 pb-8 md:pt-28 md:pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Passport Services</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
                How We Serve Tennessee
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
                From the music studios of Nashville to the logistics hubs of Memphis, from the university halls of Knoxville to the outdoor adventure corridors of Chattanooga — we help Tennessee travelers get their passports fast. The Atlanta Passport Agency is the closest regional agency, serving all of Tennessee.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: "ri-flight-takeoff-line",
                  title: "Emergency Same-Day Service",
                  desc: "For Tennessee travelers with flights within 72 hours. We hand-carry your application directly to the Atlanta Passport Agency — no road trip required from any Tennessee city.",
                },
                {
                  icon: "ri-time-line",
                  title: "Expedited 2–9 Business Day Service",
                  desc: "Standard expedited processing with overnight shipping and tracking for Tennessee residents. Perfect for summer Caribbean travel and international business trips.",
                },
                {
                  icon: "ri-file-list-3-line",
                  title: "New Passport Applications",
                  desc: "First-time applicants in Tennessee — we handle document prep, photos, and acceptance facility scheduling across all 95 counties.",
                },
                {
                  icon: "ri-refresh-line",
                  title: "Passport Renewals",
                  desc: "Tennessee renewals by mail or courier. We ensure your application is complete to avoid delays — critical before your international tour or vacation.",
                },
                {
                  icon: "ri-music-line",
                  title: "Music Industry Travel",
                  desc: "Nashville-based artists, producers, and label executives rely on us for urgent international touring passports — often within 24–48 hours.",
                },
                {
                  icon: "ri-error-warning-line",
                  title: "Lost, Stolen & Damaged",
                  desc: "Fast replacement for Tennessee residents who need a new passport quickly before international travel. Direct Atlanta Passport Agency access gets it done fast.",
                },
              ].map((svc, i) => (
                <div key={i} className="bg-white rounded-lg p-7 border border-gray-100 hover:border-[#F6C344]/40 transition-all duration-200">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-5">
                    <i className={`${svc.icon} text-[#1A365D] text-xl`}></i>
                  </div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-2">{svc.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-gray-50">
              <div className="animate-pulse text-gray-400 text-sm">Loading FAQs...</div>
            </div>
          }
        >
          <FAQSection />
        </Suspense>

        <Suspense
          fallback={
            <div className="h-64 flex items-center justify-center bg-white">
              <div className="animate-pulse text-gray-400 text-sm">Loading CTA...</div>
            </div>
          }
        >
          <FinalCTA />
        </Suspense>

        <section className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <SEOCrossLinks state="TN" showCities={false} />
          </div>
        </section>
      </div>
    </>
  );
}